import { useMemo } from "react";
import { useScaleFactor } from "./AHScaleFactor";

const radiusPrimitives = {
  "3xl": 360,
  "2xl": 128,
  xl: 32,
  lg: 16,
  md: 8,
  sm: 4,
};

const radiusTokens = {
  "radius-large": radiusPrimitives["lg"],
  "radius-full": radiusPrimitives["3xl"],
  "radius-medium": radiusPrimitives["md"],
  "radius-minimal": radiusPrimitives["sm"],
};

const spacingScale = [
  4, 8, 16, 24, 32, 40, 48, 56, 64, 72, 80, 88, 96, 104, 128, 198, 256, 320,
  384, 448, 512,
];

const spacing = {
  "spacing-2xl": spacingScale[13],
  "spacing-lg": spacingScale[4],
  "spacing-md": spacingScale[2],
  "spacing-sm": spacingScale[1],
  "spacing-xl": spacingScale[6],
  "spacing-xs": spacingScale[0],
  "spacing-none": 0,
};

export const layoutConfig: Layout = {
  radius: {
    none: 0,
    xsmall: 3,
    full: radiusTokens["radius-full"],
    small: radiusTokens["radius-minimal"],
    medium: radiusTokens["radius-medium"],
    large: radiusTokens["radius-large"],
  },
  circle: {
    dot: 6,
    badge: 9,
    xsmall: 12,
    small: 16,
    smallSmallMedium: 18,
    smallMedium: 24,
    medium: 32,
    large: 40,
    xlarge: 60,
  },
  icon: {
    xxsmall: 8,
    xsmall: 12,
    small: 16,
    medium: 20,
    large: 24,
    xlarge: 32,
    xxlarge: 40,
  },
  space: {
    none: 0,
    widget: 5,
    xxsmall: 2,
    xsmall: 4,
    small: 8,
    medium: 16,
    large: 32,
    xlarge: 48,
    xxlarge: 104,
  },
  height: {
    button: 50,
    headerItem: 30,
  },
  text: {
    detail: 10,
    body: 12,
    smallTitle: 16,
    title: 19,
    largeTitle: 30,
    xlargeTitle: 30,
    xxlargeTitle: 45,
  },
};

export type Height = "button" | "headerItem";
export type Radius = "xsmall" | "small" | "medium" | "large" | "full" | "none";
export type Space =
  | "none"
  | "widget"
  | "xxsmall"
  | "xsmall"
  | "small"
  | "medium"
  | "large"
  | "xlarge"
  | "xxlarge";

export type TextSize =
  | "detail"
  | "body"
  | "smallTitle"
  | "title"
  | "largeTitle"
  | "xlargeTitle"
  | "xxlargeTitle";

export type CircleSize =
  | "dot"
  | "smallMedium"
  | "smallSmallMedium"
  | "badge"
  | "xsmall"
  | "small"
  | "medium"
  | "large"
  | "xlarge";

export type IconSize =
  | "xxsmall"
  | "xsmall"
  | "small"
  | "medium"
  | "large"
  | "xlarge"
  | "xxlarge";

export type Layout = {
  circle: Record<CircleSize, number>;
  radius: Record<Radius, number>;
  space: Record<Space, number>;
  text: Record<TextSize, number>;
  icon: Record<IconSize, number>;
  height: Record<Height, number>;
};

export function useLayoutContext() {
  return layoutConfig;
}

export function useCircleSize(key: keyof Layout["circle"]) {
  const ctx = useLayoutContext();
  const sf = useScaleFactor();
  return useMemo(() => Math.ceil(ctx.circle[key] * sf), [ctx.circle, key, sf]);
}

export function useIconSize(key: keyof Layout["icon"]) {
  const ctx = useLayoutContext();
  return Math.floor(ctx.icon[key] * useScaleFactor());
}

function useTextSize(key: keyof Layout["text"]) {
  const ctx = useLayoutContext();
  return Math.floor(ctx.text[key] * useScaleFactor());
}

export function useSpaceSize(key: keyof Layout["space"]) {
  const ctx = useLayoutContext();
  return Math.floor(ctx.space[key] * useScaleFactor());
}

export function useRadiusSize(key: keyof Layout["radius"]) {
  const ctx = useLayoutContext();
  return Math.floor(ctx.radius[key] * useScaleFactor());
}
